import { useCallback, useMemo, useState } from 'react';
import type { CaseData } from '@/api/cases/getCases';

/** Return type of {@link useCaseMention}; also the `caseMention` prop of ChatInput. */
export interface CaseMentionState {
  /** The single case the user has hashtagged into this new chat, or `null`. */
  selectedCase: CaseData | null;
  /** Whether the search popover is currently open. */
  isPopoverOpen: boolean;
  /** Called by the ScalesIcon button in the action row, or by the `#` shortcut. */
  openPopover: () => void;
  /** Called when the user clicks outside the popover or presses Escape. */
  closePopover: () => void;
  /** Called from the popover when a search result is chosen. Also closes it. */
  selectCase: (caseData: CaseData) => void;
  /** Called when the user hits X on the chip. */
  clearCase: () => void;
}

/** State for the single case linked to a new chat, plus the popover open flag. */
export const useCaseMention = (): CaseMentionState => {
  const [selectedCase, setSelectedCase] = useState<CaseData | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const openPopover = useCallback(() => setIsPopoverOpen(true), []);
  const closePopover = useCallback(() => setIsPopoverOpen(false), []);

  const selectCase = useCallback((caseData: CaseData) => {
    setSelectedCase(caseData);
    setIsPopoverOpen(false);
  }, []);

  const clearCase = useCallback(() => {
    setSelectedCase(null);
  }, []);

  return useMemo(
    () => ({ selectedCase, isPopoverOpen, openPopover, closePopover, selectCase, clearCase }),
    [selectedCase, isPopoverOpen, openPopover, closePopover, selectCase, clearCase],
  );
};
